import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Checkbox, Divider, FormControl,
  FormControlLabel, FormHelperText, FormLabel,
  MenuItem, Select, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { API_BASE } from '../config';
import { eventYearFrom } from '../shared/eventDate';
import usePageTitle from '../shared/hooks/usePageTitle';

const POSITIONS = [
  'Classroom Teacher',
  'Administrator',
  'Dance Coach',
  'Parent/Homeschool Representative',
];

const STEPS = ['School Information', 'Confirmation'];

function InformationSection({ fee }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Thank you for your interest in Ontario Secondary School DANCEFEST. We are a not for profit
        organization led by a volunteer Board of Directors committed to supporting teachers with
        educational resources for classroom, blended or distance learning.
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        PLEASE COMPLETE THIS FORM ONLY ONCE. If you need to amend your account, please email
        ossdancefest@gmail.com.
      </Typography>
      
      </Box>
  );
}
const ACKNOWLEDGMENT_TEXT =
  'I acknowledge that my school is registering to participate in Ontario Secondary School DANCEFEST.';

const EMPTY_FORM = {
  acknowledged: false,
  schoolName: '',
  schoolAddress: '',
  city: '',
  province: '',
  postalCode: '',
  schoolBoard: '',
  registrantName: '',
  registrantEmail: '',
  alternateEmail: '',
  cellPhone: '',
  position: '',
  principalName: '',
  principalContact: '',
};

function validate(step, data) {
  const errs = {};
  if (step === 0) {
    if (!data.schoolName.trim()) errs.schoolName = 'Required';
    if (!data.schoolAddress.trim()) errs.schoolAddress = 'Required';
    if (!data.city.trim()) errs.city = 'Required';
    if (!data.province.trim()) errs.province = 'Required';
    if (!data.postalCode.trim()) errs.postalCode = 'Required';
    if (!data.schoolBoard.trim()) errs.schoolBoard = 'Required';
    if (!data.registrantName.trim()) errs.registrantName = 'Required';
    if (!data.registrantEmail.trim()) errs.registrantEmail = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.registrantEmail)) errs.registrantEmail = 'Enter a valid email address';
    if (data.alternateEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.alternateEmail)) errs.alternateEmail = 'Enter a valid email address';
    if (!data.cellPhone.trim()) errs.cellPhone = 'Required';
    if (!data.position) errs.position = 'Required';
    if (!data.principalName.trim()) errs.principalName = 'Required';
    if (!data.principalContact.trim()) errs.principalContact = 'Required';
    if (!data.acknowledged) errs.acknowledged = 'You must acknowledge this statement to submit.';
  }
  return errs;
}

export default function SchoolRegistration() {
  usePageTitle('OSSDF School Registration');
  const [step] = useState(0);
  const [data, setData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [schoolRegistrationFee, setSchoolRegistrationFee] = useState(null);
  const [eventYear, setEventYear] = useState('');

  // Pulled from INFORMATION_SHEET's Prices tab ("School Registration Fee"
  // row) so the amount shown here can't drift from what's actually charged.
  // Best-effort: if it fails to load, the sentence below just omits the
  // dollar amount rather than blocking the whole form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/prices`)
      .then(res => (res.ok ? res.json() : null))
      .then(prices => setSchoolRegistrationFee(prices?.['School Registration Fee'] ?? null))
      .catch(() => {});
  }, []);

  // Same reasoning — pulled from the Events tab so the year in the header
  // doesn't go stale every festival. Best-effort: falls back to no year
  // shown rather than blocking the form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/event`)
      .then(res => (res.ok ? res.json() : null))
      .then(event => setEventYear(eventYearFrom(event?.startDate)))
      .catch(() => {});
  }, []);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  // const handleNext = () => {
  //   const errs = validate(0, data);
  //   if (Object.keys(errs).length) { setErrors(errs); return; }
  //   setErrors({});
  //   setStep(1);
  //   window.scrollTo(0, 0);
  // };

  // const handleBack = () => {
  //   setStep(0);
  //   window.scrollTo(0, 0);
  // };

  const handleSubmit = async () => {
    const errs = validate(1, data);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${API_BASE}/api/register/school`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Submission failed');
      }
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, p: 4 }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 72 }} />
        <Typography variant="h4" fontWeight={700}>Registration Received</Typography>
        <Typography color="text.secondary" textAlign="center" maxWidth={480}>
        Thank you for registering <strong>{data.schoolName}</strong>. We will review your
          application and send your portal login credentials to{' '}
          <strong>{data.registrantEmail}</strong> once approved.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 680, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'vertical', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SchoolIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="title" fontWeight={700}>School Registration</Typography>
            <Typography variant="h5" color="text.secondary">OSSDF</Typography>
          </Box>
          <InformationSection fee={schoolRegistrationFee} />
        </Box>

        {/* Stepper */}
        <Stepper activeStep={step}>
          {STEPS.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Card elevation={2}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>

            {/* ── STEP 1 ── */}
            {step === 0 && (
              <>


                {/* School details */}
                <Typography variant="subtitle1" fontWeight={600}>School Details</Typography>
                <TextField label="Name of School" value={data.schoolName} onChange={set('schoolName')}
                  required error={!!errors.schoolName} helperText={errors.schoolName} fullWidth />
                <TextField label="Address of School" value={data.schoolAddress} onChange={set('schoolAddress')}
                  required error={!!errors.schoolAddress} helperText={errors.schoolAddress} fullWidth />
                <TextField label="City" value={data.city} onChange={set('city')}
                  required error={!!errors.city} helperText={errors.city} fullWidth />
                <TextField label="Province" value={data.province} onChange={set('province')}
                  required error={!!errors.province} helperText={errors.province} fullWidth />
                <TextField label="Postal Code" value={data.postalCode} onChange={set('postalCode')}
                  required error={!!errors.postalCode} helperText={errors.postalCode} fullWidth />
                <TextField label="Name of School Board" value={data.schoolBoard} onChange={set('schoolBoard')}
                  required error={!!errors.schoolBoard} helperText={errors.schoolBoard} fullWidth />

                <Divider />

                {/* Principal */}
                <Typography variant="subtitle1" fontWeight={600}>Principal Information</Typography>
                <TextField label="Principal Name" value={data.principalName} onChange={set('principalName')}
                  required error={!!errors.principalName} helperText={errors.principalName} fullWidth />
                <TextField label="Principal Email or Phone" value={data.principalContact} onChange={set('principalContact')}
                  required error={!!errors.principalContact} helperText={errors.principalContact} fullWidth />

                <Divider />

                {/* Registrant */}
                <Typography variant="subtitle1" fontWeight={600}>Contact Information (who should we send information to?)</Typography>
                <TextField label="Name" value={data.registrantName} onChange={set('registrantName')}
                  required error={!!errors.registrantName} helperText={errors.registrantName} fullWidth />
                <FormControl required error={!!errors.position} fullWidth>
                  <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Position</FormLabel>
                  <Select value={data.position} onChange={set('position')} displayEmpty>
                    <MenuItem value="" disabled>Select position</MenuItem>
                    {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                  {errors.position && <FormHelperText>{errors.position}</FormHelperText>}
                </FormControl>
                <TextField label="Employee Email Address" type="email" value={data.registrantEmail} onChange={set('registrantEmail')}
                  required error={!!errors.registrantEmail} helperText={errors.registrantEmail} fullWidth />
                <TextField label="Alternate Google-Compatible Email" type="email" value={data.alternateEmail} onChange={set('alternateEmail')}
                  error={!!errors.alternateEmail} helperText={errors.alternateEmail || 'Optional — can be the same as above'} fullWidth />
                <TextField label="Cell Phone Number" value={data.cellPhone} onChange={set('cellPhone')}
                  required error={!!errors.cellPhone} helperText={errors.cellPhone} fullWidth />

                <Divider />

                {/* Acknowledgment */}
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                  <Typography variant="body2">{ACKNOWLEDGMENT_TEXT}</Typography>
                </Box>
                <FormControl error={!!errors.acknowledged}>
                  <FormControlLabel
                    control={<Checkbox checked={data.acknowledged} onChange={set('acknowledged')} />}
                    label="I acknowledge this statement"
                  />
                  {errors.acknowledged && <FormHelperText>{errors.acknowledged}</FormHelperText>}
                </FormControl>



                {submitError && (
                  <Typography color="error" variant="body2">{submitError}</Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                  <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Registration'}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
